import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { routeMiles } from "./routeMiles";

// Mileage for calendar events (the FacebookEvents list), for tax records.
// Same idea as the "Mileage (taxes)" window on the Gigs page.
// Put this file next to CalendarUpdate.js (src/components/admin/).
const API_BASE_URL = "/api";

export default function EventMileageModal({ onClose }) {
    const [year, setYear] = useState(new Date().getFullYear());
    const [includeUpcoming, setIncludeUpcoming] = useState(false);
    const [includeCounted, setIncludeCounted] = useState(false);
    const [recalcAll, setRecalcAll] = useState(false);
    const [origin, setOrigin] = useState(() => {
        try { return localStorage.getItem("mileageOrigin") || ""; } catch { return ""; }
    });
    const [allEvents, setAllEvents] = useState([]);   // from the server, for the chosen year
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [message, setMessage] = useState("");
    const stop = useRef(false);

    const thisYear = new Date().getFullYear();
    const years = [thisYear, thisYear - 1, thisYear - 2];

    // Load the events for the chosen year
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        axios
            .get(`${API_BASE_URL}/Mileage/events`, { params: { year } })
            .then((res) => {
                if (cancelled) return;
                setAllEvents(Array.isArray(res.data) ? res.data : []);
                // Fill in the starting address from an earlier save if the box is empty
                const prev = (res.data || []).find((e) => e.originAddress);
                if (prev) setOrigin((o) => o || prev.originAddress);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("MILEAGE EVENTS ERROR:", err.response || err);
                setAllEvents([]);
                setMessage("Could not load events. Has the EventMileage table been created?");
            })
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [year]);

    // Build the list of rows (re-done when the year data or the checkboxes change)
    useEffect(() => {
        if (running) return;
        const nowDate = new Date();
        setRows(
            allEvents
                .filter((e) => includeUpcoming || new Date(e.start) <= nowDate)
                .map((e) => {
                    const hasAddress = !!(e.address && e.address.trim());
                    const saved = e.milesOneWay != null;
                    const skip = e.countedOnGig && !includeCounted && !saved;
                    return {
                        id: e.id,
                        date: e.start,
                        name: e.name,
                        placeName: e.placeName,
                        address: e.address,
                        rough: e.roughAddress,
                        counted: e.countedOnGig,
                        miles: saved ? String(e.milesOneWay) : "",
                        status: skip ? "counted" : saved ? "saved" : hasAddress ? "pending" : "noaddr",
                        error: ""
                    };
                })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allEvents, includeUpcoming, includeCounted]);

    const updateRow = (id, patch) =>
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

    const run = async () => {
        const from = origin.trim();
        if (!from) {
            setMessage("Enter the address you drove from.");
            return;
        }
        try { localStorage.setItem("mileageOrigin", from); } catch { /* ignore */ }

        stop.current = false;
        setRunning(true);
        setMessage("");

        const todo = rows.filter(
            (r) =>
                r.status !== "noaddr" &&
                r.status !== "counted" &&
                (recalcAll || r.status === "pending" || r.status === "error")
        );

        for (const row of todo) {
            if (stop.current) break;
            updateRow(row.id, { status: "working", error: "" });
            try {
                const res = await axios.post(`${API_BASE_URL}/Mileage/calculate-address`, {
                    address: row.address,
                    origin: from
                });
                // If the server couldn't reach the route service, the browser asks it
                const miles = res.data.needsRoute
                    ? await routeMiles(res.data.from, res.data.to)
                    : res.data.milesOneWay;
                updateRow(row.id, { status: "calculated", miles: String(miles) });
            } catch (err) {
                const reason =
                    typeof err.response?.data === "string"
                        ? err.response.data
                        : err.message || "Failed";
                updateRow(row.id, { status: "error", error: reason });
            }
        }

        setRunning(false);
        setMessage(
            stop.current
                ? "Stopped."
                : "Done. Check the numbers (you can type over any of them), then click Save."
        );
    };

    const save = async () => {
        const isNew = (r) => (r.status === "calculated" || r.status === "edited") && Number(r.miles) > 0;
        const items = rows.filter(isNew).map((r) => ({ eventId: r.id, milesOneWay: Number(r.miles) }));

        if (items.length === 0) {
            setMessage("Nothing new to save.");
            return;
        }

        try {
            await axios.post(`${API_BASE_URL}/Mileage/events/batch`, {
                originAddress: origin.trim(),
                items
            });
            setRows((prev) => prev.map((r) => (isNew(r) ? { ...r, status: "saved" } : r)));
            setMessage(`Saved ${items.length} trip${items.length === 1 ? "" : "s"}.`);
        } catch (err) {
            console.error("MILEAGE SAVE ERROR:", err);
            const reason = typeof err.response?.data === "string" ? err.response.data : "";
            setMessage(reason ? `Save failed: ${reason}` : "Save failed.");
        }
    };

    const counted = rows.filter((r) => r.status !== "counted" && Number(r.miles) > 0);
    const totals = {
        trips: counted.length,
        roundTrip: Math.round(counted.reduce((s, r) => s + Number(r.miles), 0) * 2 * 10) / 10
    };

    const exportCsv = () => {
        const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const lines = [
            ["Date", "Event", "Venue", "Venue address", "Starting address", "One-way miles", "Round-trip miles", "Purpose"].join(",")
        ];
        counted.forEach((r) => {
            const oneWay = Number(r.miles);
            lines.push(
                [
                    q(new Date(r.date).toLocaleDateString("en-CA")),
                    q(r.name),
                    q(r.placeName),
                    q(r.address),
                    q(origin),
                    oneWay.toFixed(1),
                    (Math.round(oneWay * 2 * 10) / 10).toFixed(1),
                    q("Band performance - travel to event")
                ].join(",")
            );
        });
        const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `event-mileage-${year}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const statusText = (r) => {
        switch (r.status) {
            case "saved": return "Saved";
            case "calculated": return "Calculated (not saved)";
            case "edited": return "Edited (not saved)";
            case "working": return "Calculating…";
            case "noaddr": return "No address: type the miles";
            case "counted": return "Same date as a gig already counted";
            case "error": return r.error || "Failed";
            default: return "Not calculated";
        }
    };

    return ReactDOM.createPortal(
        <div className="modal-overlay">
            <div
                className="modal"
                style={{ width: "95%", maxWidth: 900, maxHeight: "90vh", overflowY: "auto" }}
            >
                <h3>Event mileage for taxes</h3>

                <p style={{ fontSize: "0.9em", opacity: 0.8 }}>
                    Many of these shows are probably also on your Gigs page. Events on the same date as a
                    gig that already has mileage are skipped, so a trip isn't counted twice.
                </p>

                <div className="form-group">
                    <label htmlFor="evMileageOrigin">Address you drove from</label>
                    <input
                        id="evMileageOrigin"
                        value={origin}
                        onChange={(e) => setOrigin(e.target.value)}
                        placeholder="Street, city, state ZIP"
                        disabled={running}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="evMileageYear">Year</label>
                    <select
                        id="evMileageYear"
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                        disabled={running}
                    >
                        {years.map((y) => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>
                        <input type="checkbox" checked={includeUpcoming} disabled={running}
                            onChange={(e) => setIncludeUpcoming(e.target.checked)} />{" "}
                        Include upcoming events this year
                    </label>
                    <br />
                    <label>
                        <input type="checkbox" checked={includeCounted} disabled={running}
                            onChange={(e) => setIncludeCounted(e.target.checked)} />{" "}
                        Include events on the same date as a gig already counted
                    </label>
                    <br />
                    <label>
                        <input type="checkbox" checked={recalcAll} disabled={running}
                            onChange={(e) => setRecalcAll(e.target.checked)} />{" "}
                        Recalculate events that are already saved
                    </label>
                </div>

                {loading ? (
                    <p>Loading events…</p>
                ) : rows.length === 0 ? (
                    <p>No events found for {year}.</p>
                ) : (
                    <table className="calendar-update-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Event / venue</th>
                                <th>One-way miles</th>
                                <th>Round trip</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} style={r.status === "counted" ? { opacity: 0.55 } : undefined}>
                                    <td>{new Date(r.date).toLocaleDateString()}</td>
                                    <td>
                                        {r.name}
                                        <div style={{ fontSize: "0.8em", opacity: 0.7 }}>
                                            {r.address || r.placeName || ""}
                                            {r.address && r.rough ? " (rough address)" : ""}
                                        </div>
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            style={{ width: 90 }}
                                            value={r.miles}
                                            disabled={running}
                                            onChange={(e) =>
                                                updateRow(r.id, { miles: e.target.value, status: "edited" })
                                            }
                                        />
                                    </td>
                                    <td>
                                        {Number(r.miles) > 0
                                            ? (Math.round(Number(r.miles) * 2 * 10) / 10).toFixed(1)
                                            : ""}
                                    </td>
                                    <td>{statusText(r)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                <p>
                    <strong>
                        {totals.trips} trip{totals.trips === 1 ? "" : "s"},{" "}
                        {totals.roundTrip.toLocaleString()} round-trip miles in {year}
                    </strong>
                </p>

                {message && <p>{message}</p>}

                <div className="modal-buttons">
                    <button
                        type="button"
                        className="cancel-btn"
                        onClick={() => { stop.current = true; onClose(); }}
                    >
                        Close
                    </button>

                    {running ? (
                        <button type="button" className="cancel-btn" onClick={() => { stop.current = true; }}>
                            Stop
                        </button>
                    ) : (
                        <button type="button" onClick={run} disabled={loading}>
                            Calculate miles
                        </button>
                    )}

                    <button type="button" onClick={exportCsv} disabled={running || totals.trips === 0}>
                        Download CSV
                    </button>

                    <button type="button" className="save-btn" onClick={save} disabled={running}>
                        Save
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}